import React, { createContext, useEffect, useState } from "react";
import { useWallet } from "use-wallet";
import {
  getFarms,
  getRewardsPerBlock,
  getTotalAllocPoints,
  hasFarmStarted,
  isActive,
  isInEmergency,
  isPaused,
} from "../web3Utils/farm-core";
import usePrices from "../hooks/usePrices";
import useConnectWallet from "../hooks/useConnectWallet";
import { getRealContract, getRealProvider } from "../web3Utils/generateUtils";
import FarmAbi from "../web3Utils/abi/FarmAbi";

const { rpcUrl } = require("../web3Utils/configs");

const FarmsContext = createContext({
  farmConfig: null,
  globalFarmStats: null,
  initGlobalStats: null,
  isInitGlobalStatsLoaded: false,
  farms: [],
  tvl: null,
});

export const FarmsProvider = ({ farmConfig, children }) => {
  const wallet = useWallet();
  const priceProvider = usePrices();
  const connectWallet = useConnectWallet();

  const [globalFarmStats, setGlobalFarmStats] = useState({
    totalAllocationPoints: null,
    rewardsPerBlock: null,
  });
  const [isInitGlobalStatsLoaded, setIsInitGlobalStatsLoaded] = useState(false);

  const [farms, setFarms] = useState(farmConfig.farms);
  const [tvl, setTvl] = useState(null);

  useEffect(() => {
    setFarms(farmConfig.farms);
    setIsInitGlobalStatsLoaded(false);
    setGlobalFarmStats({
      totalAllocationPoints: null,
      rewardsPerBlock: null,
    });
  }, [farmConfig]);

  useEffect(() => {
    if (!isInitGlobalStatsLoaded)
      setIsInitGlobalStatsLoaded(
        globalFarmStats.totalAllocationPoints && globalFarmStats.rewardsPerBlock
      );
  }, [globalFarmStats]);

  useEffect(() => {
    if (priceProvider.loaded) initFarms();
  }, [priceProvider.loaded]);

  useEffect(() => {
    let tempTvl = 0;
    farms.forEach((farm) => {
      tempTvl = tempTvl + farm.tvl;
    });
    setTvl(tempTvl ? tempTvl : 0);
  }, [farms]);

  const initFarms = async () => {
    setFarms(
      await getFarms(
        await getRealContract(
          farmConfig.address,
          await connectWallet.getEthProvider(),
          FarmAbi,
          "place 8"
        ),
        wallet && wallet.status === "connected"
          ? wallet.ethereum
          : getRealProvider(rpcUrl),
        await initGlobalStats(),
        priceProvider,
        connectWallet.corsacPriceInUSD,
        farmConfig
      )
    );
  };

  const initGlobalStats = async (withUpdate) => {
    const realFarmContract = await getRealContract(
      farmConfig.address,
      await connectWallet.getEthProvider(),
      FarmAbi,
      "place 9"
    );

    let rewardsPerBlock = globalFarmStats.rewardsPerBlock;
    if (!globalFarmStats.rewardsPerBlock || withUpdate) {
      rewardsPerBlock = await getRewardsPerBlock(realFarmContract);
      setGlobalFarmStats({ ...globalFarmStats, rewardsPerBlock });
    }

    let totalAllocationPoints = globalFarmStats.totalAllocationPoints;
    if (!globalFarmStats.totalAllocationPoints || withUpdate) {
      totalAllocationPoints = await getTotalAllocPoints(realFarmContract);
      setGlobalFarmStats({ ...globalFarmStats, totalAllocationPoints });
    }

    const active = await isActive(realFarmContract);
    const paused = await isPaused(realFarmContract);
    const inEmergency = await isInEmergency(realFarmContract);
    const hasStarted = await hasFarmStarted(realFarmContract);
    setGlobalFarmStats({
      ...globalFarmStats,
      totalAllocationPoints,
      rewardsPerBlock,
      active,
      paused,
      hasStarted,
      inEmergency,
    });
    return {
      rewardsPerBlock,
      totalAllocationPoints,
    };
  };

  useEffect(() => {
    initGlobalStats();
  }, [farmConfig]);

  return (
    <FarmsContext.Provider
      value={{
        farmConfig,
        globalFarmStats,
        isInitGlobalStatsLoaded,
        initGlobalStats,
        farms,
        tvl,
      }}
    >
      {children}
    </FarmsContext.Provider>
  );
};

export const FarmsConsumer = FarmsContext.Consumer;

export default FarmsContext;
